import React, {useCallback, useMemo, useState} from "react";
import cx from "classnames";
import styles from "./../CustomizerPopup.module.css"
import Heading from "../../../components/Heading";
import {useTranslation} from "react-i18next";
import Input from "../../../components/Input";
import PriceOverview from "../PriceOverview";
import {CountData} from "../../../types/Individualization";
import {priceToInt} from "../../../helpers/priceToInt";
import {fabric} from "fabric";
import {useCartAdd} from "../../../hooks/useCartAdd";
import {useGlobalContext} from "../../../contexts/globalContext";
import {useCustomizer} from "../../../contexts/CustomizerContext";



const Checkout: React.FC = () => {
    const [showPricing, setShowPricing] = useState<boolean>(false)
    const [processing, setProcessing] = useState<boolean>(false)
    const {individualization, userInputs, exportTemplates, canvas, kArtikel, buttonSetting, article} = useCustomizer()
    const [quantity, setQuantity] = useState<number>(article?.min_purchase_amount ?? 1)
    const {showToast, closeModal, plugin} = useGlobalContext()
    const { cart } = useCartAdd()

    const { t } = useTranslation(['customizer'])

    const isInvalidAmount = useMemo(() => {
        return ((article?.min_purchase_amount ?? 1) > quantity) || (quantity % (article?.interval_amount ?? 1) !== 0)
    }, [quantity]);

    const graduated_prices = useMemo(() => {
        const countDataMap: Map<number, CountData[]> = new Map();
        const template_ids: number[] = [];
        const spot_ids: number[] = [];

        [...userInputs.entries()].forEach(([_id, obj]) => {
            if (obj.type === 'image' || ((obj as fabric.Text).text ?? '').length > 0) {
                if (obj.data?.template_id && !template_ids.includes(obj.data.template_id)) {
                    template_ids.push(obj.data.template_id)
                }
                if (obj.data?.spot_id && !spot_ids.includes(obj.data.spot_id)) {
                    spot_ids.push(obj.data.spot_id)
                }
            }
        })

        article?.graduated_price?.forEach((p) => {
            const current = countDataMap.get(p.count) ?? []
            const countData = {
                id: article.kArtikel,
                type: 'article',
                price: p.price
            }

            countDataMap.set(p.count, [...current, countData])
        })

        if (countDataMap.size === 0) return countDataMap;

        individualization.surcharge_graduated_price.forEach((p) => {
            const current = countDataMap.get(p.count) ?? []
            const countData = {
                id: individualization.id,
                type: 'individualization',
                price: p.price
            }
            countDataMap.set(p.count, [...current, countData])
        })

        individualization.templates.forEach((t) => {
            if (template_ids.includes(t.id)) {
                t.surcharge_graduated_price.forEach((p) => {
                    const current = countDataMap.get(p.count) ?? []
                    const countData = {
                        id: t.id,
                        type: 'template',
                        price: p.price
                    }
                    countDataMap.set(p.count, [...current, countData])
                })
            }
            t.spots.forEach((s) => {
                if (spot_ids.includes(s.id)) {
                    s.surcharge_graduated_price.forEach((p) => {
                        const current = countDataMap.get(p.count) ?? []
                        const countData = {
                            id: s.id,
                            type: 'spot',
                            price: p.price
                        }
                        countDataMap.set(p.count, [...current, countData])
                    })
                }
            })
        })

        const priceCount: Map<number, CountData[]> = new Map<number, CountData[]>()
        let previous: CountData[] = []

        for (let [count, data] of Array.from(countDataMap).sort((a,b) => {
            return a[0] - b[0]
        })) {
            const missingCounts = previous.filter((e) => {
                return data.filter((d) => {
                    return (e.id === d.id && e.type === d.type)
                }).length === 0
            })
            data = [...missingCounts, ...data]
            priceCount.set(count, data)

            previous = data
        }

        return priceCount

    }, [userInputs, individualization, article])

    const addToBasket = useCallback( () => {
        setProcessing(true)
        Promise.all(exportTemplates()).then((exportFiles) => {
            cart({
                individualization_id: individualization.id,
                kArtikel: String(kArtikel ?? individualization.article.kArtikel),
                quantity: quantity,
                images: exportFiles,
                user_inputs: [...userInputs.values()]
                        .filter((ele) => (((ele.type==='text' && (ele as fabric.Text).text?.length) || ele.type!=='text')))
                        .map((i) => ({
                            spot_id: i.data.spot_id,
                            fabric: JSON.stringify(i)
                        })),
                size: canvas.getWidth()
            }).then((r) => {
                if (r.status === 201) {
                    closeModal()
                    showToast('top-right', 'success', t('success'))
                    setTimeout(() => window.location.reload(), 2000)
                }
                setProcessing(false)
            })
        })

    }, [
        cart,
        userInputs,
        exportTemplates,
        canvas,
        closeModal,
        individualization,
        setProcessing,
        kArtikel,
        quantity,
        showToast,
        t
    ])


    const priceList = useMemo(() => {
        return [...graduated_prices.entries()].map(([count, data]) => {
            const price = data.reduce(function (acc, obj) { return acc + priceToInt(obj.price); }, 0);
            return {count, price, data}
        })
    }, [graduated_prices])

    const intToCurrency = (p: number) => {
        let currency = '€';

        if (plugin?.currency) {
            const pseudo = document.createElement('div');
            pseudo.innerHTML = plugin.currency;
            currency = pseudo.innerText;
        }

        return ((p / 100).toFixed(2) + ' ' + currency).replace('.', ',');
    }

    const price: {total: string, single: string, data: CountData[]} = useMemo(() => {
        const priceElement = priceList.sort((a, b) => b.count - a.count)
            .find((c) => {
                return c.count <= quantity
            })

        if (!priceElement) return {total: '-', single: '-', data: []}

        return {
            total: intToCurrency(priceElement.price * quantity),
            single: intToCurrency(priceElement.price),
            data: priceElement.data
        }
    }, [priceList, quantity])

    return (
        <div
            className={cx(styles['c-price-wrapper'])}
        >
                        <div className={'d-flex justify-content-between'}>
                            <span
                                onClick={() => setShowPricing(prev => !prev)}
                                className={cx(styles['c-price-ov-button'])}
                            >
                                {showPricing ? t('back') : t('overview')}
                            </span>
                            {graduated_prices.size > 1 &&
                                <span
                                    className={cx(styles['c-price-ov-button'])}
                                    onClick={() => setShowPricing(prev => !prev)}
                                >
                                    {t('discount')}
                                </span>
                            }
                        </div>
            <Heading level={3}>{t('price_per_piece',
                {sum:  price.total, quantity: quantity, single:  price.single})}
            </Heading>
            {!showPricing &&
                <>
                    <div className={cx(styles['c-price-button'], 'my-3')}>
                        <Input
                            classes={'m-0'}
                            type={'number'}
                            placeholder={t("quantity")}
                            property={'quantity'}
                            value={quantity}
                            onChange={(property, value) => setQuantity(Number(value))}
                            min={article?.min_purchase_amount}
                            step={article?.interval_amount}
                        />
                        <button
                            disabled={processing || quantity < 1 || isInvalidAmount}
                            type='button'
                            onClick={() => addToBasket()}
                            className={cx(buttonSetting?.classes, 'btn', 'btn-block', styles['zr-checkout-button'])}
                            style={{backgroundColor: buttonSetting?.background_color ?? 'transparent', color: buttonSetting?.text_color ?? 'transparent'}}
                        >{t('to_basket')}</button>
                        {(article?.min_purchase_amount ?? 1) < quantity}
                    </div>
                    {isInvalidAmount &&
                        <div className={cx(styles['c-validation-hint'])}>
                            {((article?.min_purchase_amount ?? 1) > quantity) && <div>{t('minimumPurchaseAmount', {min: article?.min_purchase_amount + (article?.unit ? ' ' + article?.unit : '')})}</div>}
                            {quantity % (article?.interval_amount ?? 1) !== 0 && <div>{t('intervalAmount', {interval: article?.interval_amount + (article?.unit ? ' '+article?.unit : '')})}</div>}
                        </div>
                    }
                </>
            }
            {showPricing &&
                <>
                    <PriceOverview
                        individualization_id={individualization.id}
                        article={article ?? individualization.article}
                        priceList={priceList}
                        priceArray={price.data}
                        templates={individualization.templates}
                        price={[price.single, price.total]}
                        quantity={quantity}
                    />
                    <div className={cx(styles['c-price-footer'])}>
                        <Heading level={3}>{t('order')}</Heading>
                        <div className={cx(styles['c-price-button'])}>
                            <Input
                                classes={'mt-1'}
                                type={'number'}
                                placeholder={t("quantity")}
                                property={'quantity'}
                                value={quantity}
                                onChange={(property, value) => setQuantity(Number(value))}
                            />
                            <button
                                type='button'
                                disabled={processing || quantity < 1}
                                onClick={() => addToBasket()}
                                className={cx(buttonSetting?.classes, 'mt-1 small-font-size btn', 'btn-block', styles['zr-checkout-button'])}
                                style={{backgroundColor: buttonSetting?.background_color ?? 'transparent', color: buttonSetting?.text_color ?? 'transparent'}}
                            >{t('to_basket')}</button>

                        </div>
                    </div>
                </>
            }
        </div>
    )
}

export default Checkout
